import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import { translate } from "app/filters/i18nFilter";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import DropdownButton from "app/pages/workspace/sales/components/custom-dropdown/DropdownButton/DropdownButton";

import CreateProfileDropdownItem from "./CreateProfileDropdownItem/CreateProfileDropdownItem";
import DropdownItem from "./DropdownItem/DropdownItem";
import ItemTooltip from "./ItemTooltip/ItemTooltip";
import { StyledDropdownBody, StyledDropdownContainer, StyledItemsContainer, StyledScrollableBody } from "./styles";

type EmployeeProfilesDropdownProps = {
    onCreateClick: () => void;
    onSelect: (profile: ProfileDto | null) => void;
    onItemSettingsClick: (profile: ProfileDto) => void;
    onOpen?: () => void;
    profiles: readonly ProfileDto[];
    selectedProfileId: string | null | undefined;
    canManageShared: boolean;
    isDisabled?: boolean;
    buttonText?: string;
};

const EmployeeProfilesDropdown = (props: EmployeeProfilesDropdownProps) => {
    const {
        onSelect,
        onCreateClick,
        onItemSettingsClick,
        onOpen,
        profiles,
        selectedProfileId,
        canManageShared,
        buttonText = "si.account-overview.employee-profiles.dropdown-button.text",
        isDisabled = false,
    } = props;

    const [isOpened, setIsOpened] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const dropdownScrollableRef = useRef<HTMLDivElement>(null);

    const selectedProfile = profiles.find((p) => p.id === selectedProfileId);
    const selectedText = selectedProfile?.name ?? null;

    const searchProps = useMemo(() => {
        return {
            value: searchTerm,
            placeholder: translate("si.account-overview.employee-profiles.dropdown.placeholder"),
            onChange: setSearchTerm,
        };
    }, [searchTerm]);

    const handleOutsideClick = () => setIsOpened(false);

    useDeactivateOnOutsideClick("employee-profiles-dropdown", isOpened, handleOutsideClick);

    useLayoutEffect(() => {
        if (isOpened) {
            dropdownScrollableRef.current?.scrollTo(0, 0);
        }
    }, [isOpened]);

    const handleItemClick = (profile: ProfileDto) => {
        setIsOpened(false);
        onSelect(profile);
    };

    const handleSelectionReset = () => {
        onSelect(null);
    };

    const handleButtonClick = () => {
        setIsOpened(true);
        onOpen?.();
    };

    const onSettingClick = (profile: ProfileDto) => {
        setIsOpened(false);
        onItemSettingsClick(profile);
    };

    // Editing a Similarweb default only ever creates a private copy, so it stays open to everyone.
    const canEditProfile = (profile: ProfileDto) => !isSharedEmployeeProfile(profile) || canManageShared;

    return (
        <StyledDropdownContainer
            className="employee-profiles-dropdown"
            data-automation="employee-profiles-dropdown-container"
        >
            <DropdownButton
                disabled={isDisabled}
                selectedText={selectedText}
                key="employee-profiles-dd-button"
                buttonText={translate(buttonText)}
                onClick={handleButtonClick}
                onClose={handleSelectionReset}
            />
            <StyledDropdownBody
                includesSearch
                open={isOpened}
                searchProps={searchProps}
            >
                <StyledScrollableBody ref={dropdownScrollableRef}>
                    {searchTerm === "" ? (
                        <CreateProfileDropdownItem
                            key="create-profile-item"
                            id="create-profile-item"
                            onClick={onCreateClick}
                        />
                    ) : null}
                    {profiles.length > 0 ? (
                        <StyledItemsContainer>
                            {profiles
                                .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                .map((p) => (
                                    <ItemTooltip
                                        key={p.id}
                                        profile={p}
                                    >
                                        <DropdownItem
                                            profile={p}
                                            isSelected={p.id === selectedProfileId}
                                            canEdit={canEditProfile(p)}
                                            onClick={handleItemClick}
                                            onSettingsClick={onSettingClick}
                                        />
                                    </ItemTooltip>
                                ))}
                        </StyledItemsContainer>
                    ) : null}
                </StyledScrollableBody>
            </StyledDropdownBody>
        </StyledDropdownContainer>
    );
};

export default EmployeeProfilesDropdown;
